import { useEffect, useMemo, useState } from 'react';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import PageHeader from '../../../shared/components/PageHeader';
import {
  createStressScenario,
  deleteStressScenario,
  generateStressAiReport,
  getLatestStressSnapshot,
  getStressAiReport,
  getStressScenario,
  listStressScenarios,
  runStressScenario,
  updateStressScenario
} from '../../../services/analysisStressTest';
import { exportStressTestPdf } from './stressTestExports';
import './StressTestPage.css';

const emptyForm = {
  name: '',
  horizonMonths: 6,
  notes: '',
  fxUsdTryPct: '',
  fxEurTryPct: '',
  tlReferenceRateBp: '',
  salesVolumePct: '',
  salesPricePct: '',
  cogsShockPct: '',
  opexShockPct: '',
  dsoDays: '',
  dioDays: '',
  dpoDays: '',
  revenueFxRatioPct: '',
  cogsFxRatioPct: '',
  debtFxRatioPct: '',
  variableRateDebtRatioPct: '',
  averageDebtTotalOverride: '',
  netFxExposureOverride: '',
  saveMappingProfileAsDefault: false
};

const toNumber = (value) => (value === '' || value === null || value === undefined ? null : Number(value));
const fmt = (value) => {
  const n = Number(value);
  return Number.isNaN(n) ? '-' : n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function StressTestPage() {
  const [selection, setSelection] = useState({ companyId: '', periodId: '' });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [snapshot, setSnapshot] = useState(null);
  const [aiReport, setAiReport] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  const hasSelection = Boolean(selection.companyId && selection.periodId);

  const loadList = async () => {
    if (!selection.companyId) {
      setList([]);
      return;
    }
    const rows = await listStressScenarios(selection.companyId);
    setList(rows || []);
  };

  useEffect(() => {
    loadList().catch((error) => setMessage(error.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.companyId]);

  const applyScenarioToForm = (scenario) => {
    if (!scenario) {
      setForm(emptyForm);
      return;
    }
    const d = scenario.drivers || {};
    const p = scenario.scenarioMappingProfileOverride || {};
    setForm({
      name: scenario.name || '',
      horizonMonths: scenario.horizonMonths || 6,
      notes: scenario.notes || '',
      fxUsdTryPct: d.fxUsdTryPct ?? '',
      fxEurTryPct: d.fxEurTryPct ?? '',
      tlReferenceRateBp: d.tlReferenceRateBp ?? '',
      salesVolumePct: d.salesVolumePct ?? '',
      salesPricePct: d.salesPricePct ?? '',
      cogsShockPct: d.cogsShockPct ?? '',
      opexShockPct: d.opexShockPct ?? '',
      dsoDays: d.dsoDays ?? '',
      dioDays: d.dioDays ?? '',
      dpoDays: d.dpoDays ?? '',
      revenueFxRatioPct: p.revenueFxRatioPct ?? '',
      cogsFxRatioPct: p.cogsFxRatioPct ?? '',
      debtFxRatioPct: p.debtFxRatioPct ?? '',
      variableRateDebtRatioPct: p.variableRateDebtRatioPct ?? '',
      averageDebtTotalOverride: p.averageDebtTotalOverride ?? '',
      netFxExposureOverride: p.netFxExposureOverride ?? '',
      saveMappingProfileAsDefault: false
    });
  };

  const handleOpen = async (id) => {
    setLoading(true);
    setMessage('');
    try {
      const scenario = await getStressScenario(id);
      setActiveScenarioId(id);
      applyScenarioToForm(scenario);
      const latest = await getLatestStressSnapshot(id).catch(() => null);
      setSnapshot(latest);
      if (latest?.id) {
        const report = await getStressAiReport(latest.id).catch(() => null);
        setAiReport(report);
      } else {
        setAiReport(null);
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const scenarioPayload = useMemo(() => ({
    companyId: Number(selection.companyId),
    basePeriodId: Number(selection.periodId),
    name: form.name,
    horizonMonths: Number(form.horizonMonths || 6),
    notes: form.notes,
    fxUsdTryPct: toNumber(form.fxUsdTryPct),
    fxEurTryPct: toNumber(form.fxEurTryPct),
    tlReferenceRateBp: toNumber(form.tlReferenceRateBp),
    salesVolumePct: toNumber(form.salesVolumePct),
    salesPricePct: toNumber(form.salesPricePct),
    cogsShockPct: toNumber(form.cogsShockPct),
    opexShockPct: toNumber(form.opexShockPct),
    dsoDays: toNumber(form.dsoDays),
    dioDays: toNumber(form.dioDays),
    dpoDays: toNumber(form.dpoDays),
    mappingProfile: {
      revenueFxRatioPct: toNumber(form.revenueFxRatioPct),
      cogsFxRatioPct: toNumber(form.cogsFxRatioPct),
      debtFxRatioPct: toNumber(form.debtFxRatioPct),
      variableRateDebtRatioPct: toNumber(form.variableRateDebtRatioPct),
      averageDebtTotalOverride: toNumber(form.averageDebtTotalOverride),
      netFxExposureOverride: toNumber(form.netFxExposureOverride)
    },
    saveMappingProfileAsDefault: Boolean(form.saveMappingProfileAsDefault)
  }), [form, selection.companyId, selection.periodId]);

  const saveScenario = async () => {
    if (!hasSelection) {
      setMessage('Lutfen sirket ve donem secin.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      if (activeScenarioId) {
        await updateStressScenario(activeScenarioId, scenarioPayload);
      } else {
        const created = await createStressScenario(scenarioPayload);
        setActiveScenarioId(created.id);
      }
      await loadList();
      setMessage('Senaryo kaydedildi.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const removeScenario = async (id) => {
    setLoading(true);
    setMessage('');
    try {
      await deleteStressScenario(id);
      if (activeScenarioId === id) {
        setActiveScenarioId(null);
        setSnapshot(null);
        setAiReport(null);
      }
      await loadList();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const runScenario = async (id) => {
    setLoading(true);
    setMessage('');
    try {
      await runStressScenario(id);
      const latest = await getLatestStressSnapshot(id);
      setSnapshot(latest);
      setAiReport(null);
      await loadList();
      setMessage('Senaryo calistirildi.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const regenerateAi = async () => {
    if (!snapshot?.id) {
      setMessage('AI raporu icin once senaryo calistirin.');
      return;
    }
    setLoading(true);
    try {
      const report = await generateStressAiReport(snapshot.id, { locale: 'tr-TR', temperature: 0.2 });
      setAiReport(report);
      setActiveTab('ai');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const compareRows = list.filter((item) => selectedIds.includes(item.id));
  const comparison = compareRows.length >= 2 ? compareRows : [];
  const baseFinancials = snapshot?.baseFinancials || {};
  const scnFinancials = snapshot?.scenarioFinancials || {};
  const kpi = snapshot?.kpi || {};
  const attributionItems = (snapshot?.attribution?.items || []);

  const handleExportPdf = async () => {
    if (!snapshot) {
      setMessage('PDF icin once senaryo calistirin.');
      return;
    }
    try {
      await exportStressTestPdf({
        scenarioName: form.name || list.find((x) => x.id === activeScenarioId)?.name || 'Senaryo',
        companyName: selection.company?.name || '-',
        periodLabel: selection.period?.label || selection.period?.code || '-',
        generatedAt: new Date().toISOString(),
        snapshot,
        aiReportText: aiReport?.reportText || ''
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'PDF olusturulamadi.');
    }
  };

  return (
    <div className="card stress-test-card">
      <PageHeader
        title="ScenarioLab - Finansal Etki Simulasyonu"
        description="Driver soklari ile pro-forma finansallari, KPI farklarini, attribution ve AI aksiyon raporunu uretir."
        actions={(
          <button type="button" className="btn-primary" onClick={handleExportPdf} disabled={loading || !snapshot}>
            PDF Indir
          </button>
        )}
      />

      <CompanyPeriodSelector onSelectionChange={setSelection} />
      {message && <p className="stress-message">{message}</p>}

      <div className="stress-grid">
        <section className="stress-list">
          <div className="section-head"><h3>Senaryo Listesi</h3></div>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Ad</th>
                <th>Horizon</th>
                <th>Olusturma</th>
                <th>Son Calistirma</th>
                <th>Durum</th>
                <th>Aksiyon</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={7}>Kayit yok.</td></tr>
              ) : list.map((item) => (
                <tr key={item.id} className={activeScenarioId === item.id ? 'active' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={(event) => setSelectedIds((prev) => event.target.checked ? [...new Set([...prev, item.id])] : prev.filter((x) => x !== item.id))}
                    />
                  </td>
                  <td>{item.name}</td>
                  <td>{item.horizonMonths} ay</td>
                  <td>{item.createdAt ? new Date(item.createdAt).toLocaleString('tr-TR') : '-'}</td>
                  <td>{item.lastRunAt ? new Date(item.lastRunAt).toLocaleString('tr-TR') : '-'}</td>
                  <td>{item.lastRunStatus || '-'}</td>
                  <td className="row-actions">
                    <button type="button" onClick={() => handleOpen(item.id)}>Ac</button>
                    <button type="button" onClick={() => { setActiveScenarioId(null); setForm((prev) => ({ ...prev, name: `${item.name} - Kopya` })); }}>Kopyala</button>
                    <button type="button" onClick={() => runScenario(item.id)}>Calistir</button>
                    <button type="button" onClick={() => removeScenario(item.id)}>Sil</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {comparison.length >= 2 && (
            <div className="compare-box">
              <strong>Karsilastirma</strong>
              <p>{comparison.map((x) => `${x.name} (${x.lastRunStatus || '-'})`).join(' | ')}</p>
            </div>
          )}
        </section>

        <section className="stress-form">
          <div className="section-head"><h3>Senaryo Olustur / Duzenle</h3></div>
          <div className="form-grid">
            <label>Senaryo Adi<input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></label>
            <label>Horizon (1-24)<input type="number" min="1" max="24" value={form.horizonMonths} onChange={(e) => setForm((p) => ({ ...p, horizonMonths: e.target.value }))} /></label>
            <label className="full">Notlar<textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} /></label>
            <label>USD/TRY %<input type="number" value={form.fxUsdTryPct} onChange={(e) => setForm((p) => ({ ...p, fxUsdTryPct: e.target.value }))} /></label>
            <label>EUR/TRY %<input type="number" value={form.fxEurTryPct} onChange={(e) => setForm((p) => ({ ...p, fxEurTryPct: e.target.value }))} /></label>
            <label>Faiz bp (baz puan)<input type="number" value={form.tlReferenceRateBp} onChange={(e) => setForm((p) => ({ ...p, tlReferenceRateBp: e.target.value }))} /></label>
            <label>Hacim %<input type="number" value={form.salesVolumePct} onChange={(e) => setForm((p) => ({ ...p, salesVolumePct: e.target.value }))} /></label>
            <label>Fiyat %<input type="number" value={form.salesPricePct} onChange={(e) => setForm((p) => ({ ...p, salesPricePct: e.target.value }))} /></label>
            <label>COGS % (satis maliyeti)<input type="number" value={form.cogsShockPct} onChange={(e) => setForm((p) => ({ ...p, cogsShockPct: e.target.value }))} /></label>
            <label>OPEX % (faaliyet gideri)<input type="number" value={form.opexShockPct} onChange={(e) => setForm((p) => ({ ...p, opexShockPct: e.target.value }))} /></label>
            <label>DSO (gun) (alacak tahsil suresi)<input type="number" value={form.dsoDays} onChange={(e) => setForm((p) => ({ ...p, dsoDays: e.target.value }))} /></label>
            <label>DIO (gun) (stokta kalma suresi)<input type="number" value={form.dioDays} onChange={(e) => setForm((p) => ({ ...p, dioDays: e.target.value }))} /></label>
            <label>DPO (gun) (borc odeme suresi)<input type="number" value={form.dpoDays} onChange={(e) => setForm((p) => ({ ...p, dpoDays: e.target.value }))} /></label>
            <label>Revenue FX % (gelirin doviz etkisi)<input type="number" value={form.revenueFxRatioPct} onChange={(e) => setForm((p) => ({ ...p, revenueFxRatioPct: e.target.value }))} /></label>
            <label>COGS FX % (maliyetin doviz etkisi)<input type="number" value={form.cogsFxRatioPct} onChange={(e) => setForm((p) => ({ ...p, cogsFxRatioPct: e.target.value }))} /></label>
            <label>Debt FX % (borcun doviz etkisi)<input type="number" value={form.debtFxRatioPct} onChange={(e) => setForm((p) => ({ ...p, debtFxRatioPct: e.target.value }))} /></label>
            <label>Variable Debt % (degisken faizli borc)<input type="number" value={form.variableRateDebtRatioPct} onChange={(e) => setForm((p) => ({ ...p, variableRateDebtRatioPct: e.target.value }))} /></label>
            <label>Ortalama Borc Override<input type="number" value={form.averageDebtTotalOverride} onChange={(e) => setForm((p) => ({ ...p, averageDebtTotalOverride: e.target.value }))} /></label>
            <label>Net FX Pozisyon<input type="number" value={form.netFxExposureOverride} onChange={(e) => setForm((p) => ({ ...p, netFxExposureOverride: e.target.value }))} /></label>
            <label className="full checkbox-row">
              <input type="checkbox" checked={form.saveMappingProfileAsDefault} onChange={(e) => setForm((p) => ({ ...p, saveMappingProfileAsDefault: e.target.checked }))} />
              Sirket varsayilan profiline kaydet
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn-primary" disabled={loading} onClick={saveScenario}>Kaydet</button>
            {activeScenarioId && <button type="button" disabled={loading} onClick={() => runScenario(activeScenarioId)}>Calistir</button>}
          </div>
        </section>
      </div>

      <section className="stress-output">
        <div className="section-head">
          <h3>Sonuc</h3>
          <div className="tab-actions">
            <button type="button" onClick={() => setActiveTab('summary')}>Ozet</button>
            <button type="button" onClick={() => setActiveTab('proforma')}>Pro-forma</button>
            <button type="button" onClick={() => setActiveTab('attribution')}>Attribution</button>
            <button type="button" onClick={() => setActiveTab('ai')}>AI Raporu</button>
            <button type="button" onClick={regenerateAi}>AI Yeniden Uret</button>
          </div>
        </div>

        {activeTab === 'summary' && (
          <div className="kpi-grid">
            <article><span>Net Kar</span><strong>{fmt(kpi?.base?.netProfit)} / {fmt(kpi?.scenario?.netProfit)}</strong></article>
            <article><span>EBIT</span><strong>{fmt(kpi?.base?.ebit)} / {fmt(kpi?.scenario?.ebit)}</strong></article>
            <article><span>Brut Marj</span><strong>{fmt((kpi?.base?.grossMargin || 0) * 100)}% / {fmt((kpi?.scenario?.grossMargin || 0) * 100)}%</strong></article>
            <article><span>Faiz Karsilama</span><strong>{fmt(kpi?.base?.interestCoverage)} / {fmt(kpi?.scenario?.interestCoverage)}</strong></article>
            <article><span>Cari Oran</span><strong>{fmt(kpi?.base?.currentRatio)} / {fmt(kpi?.scenario?.currentRatio)}</strong></article>
            <article><span>Net Borc</span><strong>{fmt(kpi?.base?.netDebt)} / {fmt(kpi?.scenario?.netDebt)}</strong></article>
          </div>
        )}

        {activeTab === 'proforma' && (
          <table>
            <thead><tr><th>Kalem</th><th>Baz</th><th>Senaryo</th><th>Fark</th></tr></thead>
            <tbody>
              {['revenue', 'cogs', 'opex', 'interestCost', 'fxDifference', 'ebit', 'netProfit', 'currentAssets', 'currentLiabilities', 'netDebt'].map((key) => (
                <tr key={key}>
                  <td>{key}</td>
                  <td>{fmt(baseFinancials[key])}</td>
                  <td>{fmt(scnFinancials[key])}</td>
                  <td>{fmt((Number(scnFinancials[key] || 0) - Number(baseFinancials[key] || 0)))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'attribution' && (
          <table>
            <thead><tr><th>Driver</th><th>Etki (TL)</th><th>Etki (%)</th></tr></thead>
            <tbody>
              {attributionItems.length === 0 ? <tr><td colSpan={3}>Attribution verisi yok.</td></tr> : attributionItems.map((item, idx) => (
                <tr key={`${item.driver}-${idx}`}>
                  <td>{item.driver}</td>
                  <td>{fmt(item.impactTl)}</td>
                  <td>{fmt((Number(item.impactPct || 0) * 100))}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'ai' && (
          <div className="ai-report">
            <pre>{aiReport?.reportText || 'AI raporu yok. "AI Yeniden Uret" butonunu kullanin.'}</pre>
          </div>
        )}
      </section>
    </div>
  );
}

export default StressTestPage;
